/**
 * IMPORTS
 */

import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useStoreZustandExpedicoes } from "@/store-zustand/expedition/expedition";
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { handleGetExpedition } from "@/domain/use-cases/expedicoes";
import { capitalize } from "@/utils/format-capitalize";
import { getStatusLabel } from "@/utils/get-status-label";

export interface ICliente {
  id: number;
  nome: string;
  email: string;
  cnpj: string;
  cpf: string;
  documento?: string | null;
  ativo: boolean;
  tipo_pessoa: string;
  telefone: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface ISolicitante {
  id: number;
  remessa_id: number;
  user_id: number;
  nome: string;
  email?: string;
  documento?: string | null;
  cpf: string;
  telefone?: string;
  created_at: string;
  updated_at: string;
}

interface IConsultor {
  id: number;
  nome: string;
  cliente_nome?: string;
  email: string;
  cpf: string;
  telefone: string;
  created_at: string;
  updated_at: string;
}
export interface ITecnologia {
  id: number;
  nome: string;
  descricao: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export interface TipoEntrega {
  id: number;
  cliente_id: number;
  tipo_entrega_id: number;
  created_at: Date;
  updated_at: Date;
}

export interface IModeloTecnico {
  id: number;
  user_id: number;
  produto_id: number;
  tecnologia_id: number;
  nome_modelo: string;
  tipo_entrega: string;
  posicionamento: string;
  tem_furo: number;
  tipo_furo: string | null;
  tem_carga_foto: number;
  tem_dados_variaveis: number;
  campo_chave: string;
  foto_frente_path: string | null;
  foto_verso_path: string | null;
  observacoes: string;
  created_at: string;
  updated_at: string;
}

export interface IRemessa {
  id: number;
  total_solicitacoes: number;
  situacao: string;
  data_remessa: string;
  data_inicio_producao: string;
  posicao: string;
  prazo: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  cliente: ICliente;
  tecnologia: ITecnologia;
  modelo_tecnico: IModeloTecnico;
  solicitante: ISolicitante;
  consultor?: IConsultor; // opcional, você usa no map
  tipo_entrega?: TipoEntrega;
  responsavel?: string; // idem
  designer: any;
  observacao: string | null;
  numero_remessa: string;
}

export interface IExpeditionResponse {
  code: number;
  status: string;
  message: string;
  data: IRemessa[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export function generatePagination(
  currentPage: number,
  lastPage: number,
  delta = 2
): (number | string)[] {
  const range: (number | string)[] = [];
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(lastPage - 1, currentPage + delta);

  range.push(1);

  if (left > 2) {
    range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < lastPage - 1) {
    range.push("...");
  }

  if (lastPage > 1) {
    range.push(lastPage);
  }

  return range;
}

function mapExpedicaoToTable(data: IRemessa[]): Record<string, any>[] {
  return data?.map((remessa) => ({
    id: remessa.id,
    remessa: remessa.id,
    numero_remessa: remessa.numero_remessa ?? "N/A",
    nome_identificacao: remessa.cliente?.nome ?? "N/A",
    email: remessa.cliente?.email ?? "N/A",
    quantidadeSolicitacao: remessa.total_solicitacoes ?? 0,
    status: getStatusLabel(remessa.situacao) ?? "N/A",
    data: remessa.created_at ? new Date(remessa.created_at).toLocaleString("pt-BR") : "N/A",
    prazo: `${remessa.prazo > 1 ? `${remessa.prazo} dias` : `${remessa.prazo} dia`}`,
    posicao: remessa.posicao ?? "N/A",
    tecnologia: remessa.tecnologia?.nome ?? "N/A",
    modelo: remessa.modelo_tecnico?.nome_modelo ?? "N/A",
    modelo_id: remessa.modelo_tecnico?.id ?? null,
    furo: remessa.modelo_tecnico?.tem_furo
      ? capitalize(remessa.modelo_tecnico?.tipo_furo ?? "Sim")
      : "Não",
    responsavel: remessa?.solicitante?.nome ?? "N/A", // idem
    cliente: remessa.cliente.nome ?? "N/A",
    cliente_id: remessa.cliente.id,
    designer: remessa?.designer?.nome ?? "N/A",
    tipo_entrega: remessa?.tipo_entrega ?? "N/A",
    observacao: remessa.observacao ?? "",
    hasMensagem: Boolean(remessa.observacao !== null),
    // dados brutos usados no modal de detalhes da remessa
    remessaCompleta: remessa,
  }));
}

function useTarefasEmExpedicoes() {
  const { currentPage, itemsPerPage, searchItem, setPaginations, setLastPage, setTotalItemsPage } =
    useStoreZustandExpedicoes();

  const { token: accessToken } = useStoreZustandUserAuth();

  // Chave de cache única baseada na página atual e na quantidade de itens por página
  const queryKey = ["expedicoes", currentPage, itemsPerPage, searchItem];

  const {
    isLoading,
    error: isError,
    data: expedicoesResponse,
  } = useQuery<IExpeditionResponse>({
    queryKey,
    queryFn: async () => {
      const response = await handleGetExpedition(
        accessToken!,
        currentPage,
        itemsPerPage,
        searchItem!
      );

      return response;
    },
  });

  const expedicaoMapeada = mapExpedicaoToTable(expedicoesResponse?.data as IRemessa[]);

  const pagination = expedicoesResponse?.pagination;

  // ✅ só recalcula se currentPage ou last_page mudar
  const pages = useMemo(() => {
    return generatePagination(currentPage, pagination?.last_page ?? 1);
  }, [currentPage, pagination?.last_page]);

  // ✅ atualiza Zustand uma única vez
  useEffect(() => {
    setPaginations(pages as number[]);
    setLastPage(pagination?.last_page as number);
    setTotalItemsPage(Number(pagination?.total));
  }, [pages, setPaginations, setLastPage, pagination, setTotalItemsPage]);

  return {
    tarefasEmExpedicoes: expedicaoMapeada ?? [],
    paginations: pages,
    isLoading,
    isError,
  };
}

/**
 * EXPORTS
 */
export { useTarefasEmExpedicoes };
